import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  HiOutlineBuildingStorefront, HiOutlinePhoto,
  HiOutlineMapPin, HiOutlineArrowLeftCircle,
  HiOutlinePencilSquare, HiOutlinePlusCircle
} from 'react-icons/hi2';
import api from '../utils/api';
import TopBar from '../components/TopBar';
import { useRestaurant } from '../context/RestaurantContext';

const empty = { name: '', logo: '', location: '', address: '', phone: '', currency: '£' };

const CURRENCIES = [
  { value: '£', label: '£ GBP' },
  { value: '€', label: '€ EUR' }
];

const RestaurantForm = () => {
  const nav = useNavigate();
  const { restaurantId } = useParams();
  const isEdit = Boolean(restaurantId);
  const { refreshRestaurants } = useRestaurant();

  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  // Load existing restaurant when editing
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await api.get('/restaurants');
        const found = (r.data.restaurants || []).find(x => x._id === restaurantId);
        if (!found) {
          toast.error('Restaurant not found');
          nav('/restaurants/manage');
          return;
        }
        setForm({
          name: found.name || '',
          logo: found.logo || '',
          location: found.location || '',
          address: found.address || '',
          phone: found.phone || '',
          currency: found.currency || '£'
        });
      } catch {
        toast.error('Failed to load restaurant');
        nav('/restaurants/manage');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, restaurantId, nav]);

  const onPickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error('Logo must be smaller than 500 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm(p => ({ ...p, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const r = await api.put(`/restaurants/${restaurantId}`, form);
        if (r.data.success) toast.success('Restaurant updated');
      } else {
        const r = await api.post('/restaurants', form);
        if (r.data.success) toast.success('Restaurant created');
      }
      await refreshRestaurants();
      nav('/restaurants/manage');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <TopBar title={isEdit ? 'Edit Restaurant' : 'New Restaurant'} />
        <div className="empty">Loading…</div>
      </div>
    );
  }

  const heroIcon = isEdit ? <HiOutlinePencilSquare size={26} /> : <HiOutlinePlusCircle size={26} />;
  const heroTitle = isEdit ? `Edit ${form.name || 'Restaurant'}` : 'Add Restaurant';
  const heroSub = isEdit
    ? 'Update logo, location, contact details, and currency for this restaurant.'
    : 'Add a new restaurant location. You can manage staff and shifts for it once created.';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <TopBar title={isEdit ? 'Edit Restaurant' : 'New Restaurant'} />

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, var(--ink-900) 0%, var(--ink-800) 100%)',
        color: '#fff',
        padding: '32px 24px 76px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background:
            'radial-gradient(ellipse 60% 50% at 95% -20%, rgba(155, 242, 19, 0.14) 0%, transparent 60%),' +
            'radial-gradient(ellipse 60% 50% at 0% 120%, rgba(28, 92, 255, 0.18) 0%, transparent 55%)',
          pointerEvents: 'none'
        }} />
        <div style={{ maxWidth: 720, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <button onClick={() => nav('/restaurants/manage')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.08)', color: '#fff',
              border: '1.5px solid rgba(255,255,255,0.16)',
              padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', marginBottom: 18,
              transition: 'background 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
          >
            <HiOutlineArrowLeftCircle size={14} /> Back to restaurants
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: 'var(--blue-500)',
              color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 0 0 4px rgba(28,92,255,0.18), 0 6px 24px rgba(28,92,255,0.4)'
            }}>
              {heroIcon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                margin: 0, fontSize: 28, fontWeight: 800,
                letterSpacing: '-0.025em', color: '#fff', lineHeight: 1.1
              }}>{heroTitle}</h1>
              <p style={{
                margin: '6px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.5, maxWidth: 540
              }}>{heroSub}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form card overlapping hero */}
      <div className="page" style={{ marginTop: -40, position: 'relative', zIndex: 2, paddingTop: 0, paddingBottom: 32 }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <form onSubmit={onSubmit} className="card" style={{ padding: 28 }}>

            {/* Logo uploader */}
            <div className="field" style={{ marginBottom: 22 }}>
              <label className="label">Logo</label>
              <div style={{
                display: 'flex', gap: 16, alignItems: 'center',
                padding: 16,
                background: 'var(--paper-soft)',
                border: '1.5px dashed var(--border-strong)',
                borderRadius: 'var(--r)'
              }}>
                <div style={{
                  width: 80, height: 80, borderRadius: 14, flexShrink: 0,
                  background: form.logo
                    ? `url(${form.logo}) center/cover`
                    : 'linear-gradient(135deg, var(--ink-900), var(--ink-700))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid var(--border)'
                }}>
                  {!form.logo && <HiOutlinePhoto size={32} color="var(--lime-400)" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input ref={fileRef} type="file" accept="image/*"
                    onChange={onPickLogo} style={{ display: 'none' }} />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-primary btn-sm"
                      onClick={() => fileRef.current?.click()}>
                      {form.logo ? 'Change logo' : 'Upload logo'}
                    </button>
                    {form.logo && (
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => setForm(p => ({ ...p, logo: '' }))}
                        style={{ color: 'var(--bad)' }}>
                        Remove
                      </button>
                    )}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>
                    PNG or JPG, max 500 KB. Square images look best.
                  </p>
                </div>
              </div>
            </div>

            {/* Name + Location side-by-side on desktop */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16
            }}>
              <div className="field">
                <label className="label">Restaurant Name <span style={{ color: 'var(--bad)' }}>*</span></label>
                <div className="field-shell">
                  <span className="field-shell-icon"><HiOutlineBuildingStorefront /></span>
                  <input type="text" placeholder="e.g. Chickn Lickn"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    required />
                </div>
              </div>

              <div className="field">
                <label className="label">Location <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(city/area)</span></label>
                <div className="field-shell">
                  <span className="field-shell-icon"><HiOutlineMapPin /></span>
                  <input type="text" placeholder="e.g. Belfast"
                    value={form.location}
                    onChange={e => setForm(p => ({ ...p, location: e.target.value }))} />
                </div>
              </div>
            </div>

            {/* <div className="field" style={{ marginTop: 16 }}>
              <label className="label">Full address <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <div className="field-shell">
                <span className="field-shell-icon"><HiOutlineHome /></span>
                <input type="text" placeholder="123 High Street, Belfast BT1 1AA"
                  value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
              </div>
            </div> */}

            {/* Phone + Currency side-by-side */}
            <div className="field" style={{ marginTop: 16 }}>
              <label className="label">Currency</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, maxWidth: 300 }}>
                {CURRENCIES.map(c => {
                  const active = form.currency === c.value;
                  return (
                    <button key={c.value} type="button"
                      onClick={() => setForm(p => ({ ...p, currency: c.value }))}
                      style={{
                        padding: '10px 8px',
                        background: active ? 'var(--blue-500)' : 'var(--surface)',
                        color: active ? '#fff' : 'var(--text)',
                        border: `1.5px solid ${active ? 'var(--blue-500)' : 'var(--border-strong)'}`,
                        borderRadius: 'var(--r-sm)',
                        fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', fontFamily: 'inherit',
                        transition: 'all 0.15s'
                      }}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Action bar */}
            <div style={{
              display: 'flex', gap: 10, justifyContent: 'flex-end',
              marginTop: 28, paddingTop: 22, borderTop: '1px solid var(--border)',
              flexWrap: 'wrap'
            }}>
              <button type="button" className="btn btn-ghost"
                onClick={() => nav('/restaurants/manage')}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary"
                disabled={saving} style={{ minWidth: 180 }}>
                {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create restaurant')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RestaurantForm;